import CRUDService from 'src/services/CRUDService';
import {UserType} from 'src/types/enums';
import {showHSAccountConversionTab} from 'src/views/high-school/account-settings/utils';
import FeatureFlagService from '../../../services/FeatureFlagService';
import {IconDefinition} from '@fortawesome/fontawesome-common-types';
import {
  faBell,
  faGraduationCap,
  faInbox,
  faLock,
  faLockAlt,
  faQuestionCircle,
  faShieldCheck,
  faUser,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';

type Path =
  | 'colleagues'
  | 'connect'
  | 'contact'
  | 'convert'
  | 'manage-data-sharing'
  | 'manage-email-messaging'
  | 'messages'
  | 'email'
  | 'help'
  | 'name'
  | 'notifications'
  | 'password'
  | 'privacy'
  | 'profile'
  | 'spot-awards'
  | 'manage-students'
  | 'trust-and-verification';

export interface AccountSettingsTab {
  label: string;
  path: string;
  mobileIcon?: IconDefinition;
  portals: UserType[];
  renderIf?: Partial<Record<UserType, () => Promise<$TSFixMe>>>; // functions run to determine if a route/tab should be rendered in a particular user portal
}

interface HighSchoolInfo {
  _id: string;
  highSchoolInfo: {
    graduationYear: number;
    lunchPlan: $TSFixMe;
    senior: boolean;
  };
}
const HighSchoolSettingsService = new CRUDService<HighSchoolInfo>('/v1/users');

/*
This object is now used for both routing and rendering account setting tabs. Order matters.

Currently used for:
- All
  - Label and path are used for every desktop and ionic account settings page
- Portals and renderIf are used for
  - Rendering account setting routes in all the _-mobile-application pages
  - Rendering which tabs you can choose on the ionic /more pages
- Component is used for rendering the ionic account setting routes
TODO: if appropriate, other account settings pages could easily be moved to use this logic for rendering as well
*/
export const ACCOUNT_SETTING_ROUTES: Record<Path, AccountSettingsTab> = {
  profile: {
    label: 'Edit Profile',
    path: '/account-settings/profile',
    mobileIcon: faUser,
    portals: [
      UserType.HS_STUDENT,
      UserType.CC_STUDENT,
      UserType.PARENT,
      UserType.UNDERGRADUATE_STUDENT,
      UserType.ADULT_LEARNER,
      UserType.EDUCATOR,
    ],
  },
  contact: {
    label: 'Email & Mobile',
    path: '/account-settings/contact',
    mobileIcon: faShieldCheck,
    portals: [
      UserType.HS_STUDENT,
      UserType.CC_STUDENT,
      UserType.PARENT,
      UserType.UNDERGRADUATE_STUDENT,
      UserType.ADULT_LEARNER,
      UserType.EDUCATOR,
    ],
  },
  email: {label: 'Email', path: '/account-settings/email', portals: []},
  ['trust-and-verification']: {
    label: 'Trust and Verification',
    path: '/account-settings/trust-and-verification',
    portals: [UserType.EDUCATOR],
  },
  colleagues: {
    label: 'Manage Colleagues',
    path: '/account-settings/colleagues',
    portals: [UserType.EDUCATOR],
  },
  privacy: {
    label: 'Privacy Settings',
    path: '/account-settings/privacy',
    mobileIcon: faLock,
    portals: [UserType.HS_STUDENT, UserType.CC_STUDENT],
  },
  ['manage-students']: {
    label: 'Manage Students',
    path: '/account-settings/manage-students',
    mobileIcon: faUsers,
    portals: [UserType.PARENT],
  },
  connect: {
    label: 'Manage Connections',
    path: '/account-settings/connect',
    mobileIcon: faUsers,
    portals: [UserType.HS_STUDENT, UserType.CC_STUDENT],
  },
  convert: {
    label: 'Conversion',
    path: '/account-settings/convert',
    mobileIcon: faGraduationCap,
    portals: [UserType.HS_STUDENT],
    renderIf: {
      [UserType.HS_STUDENT]: async (): Promise<boolean> => {
        const highSchoolInfo = await HighSchoolSettingsService.show('me', {
          fields: 'highSchoolInfo',
        });
        return showHSAccountConversionTab(highSchoolInfo.highSchoolInfo.graduationYear);
      },
    },
  },
  ['manage-email-messaging']: {
    label: 'Email Messaging Aliases',
    path: '/account-settings/manage-email-aliases',
    portals: [],
  },
  ['manage-data-sharing']: {
    label: 'Manage Data Sharing',
    path: '/account-settings/manage-data-sharing',
    portals: [],
  },
  name: {label: 'Name', path: '/account-settings/name', portals: []},
  messages: {
    label: 'Messages',
    path: '/messages',
    portals: [UserType.HS_STUDENT],
    mobileIcon: faInbox,
    renderIf: {
      [UserType.HS_STUDENT]: () => FeatureFlagService.getFeatureFlagStatus('studentMessaging'),
    },
  },
  password: {
    label: 'Change Password',
    path: '/account-settings/password',
    mobileIcon: faLockAlt,
    portals: [
      UserType.HS_STUDENT,
      UserType.CC_STUDENT,
      UserType.PARENT,
      UserType.UNDERGRADUATE_STUDENT,
      UserType.ADULT_LEARNER,
      UserType.EDUCATOR,
    ],
  },
  notifications: {
    label: 'Notification Settings',
    path: '/account-settings/notifications',
    mobileIcon: faBell,
    portals: [
      UserType.HS_STUDENT,
      UserType.CC_STUDENT,
      UserType.PARENT,
      UserType.UNDERGRADUATE_STUDENT,
      UserType.ADULT_LEARNER,
      UserType.EDUCATOR,
    ],
  },
  ['spot-awards']: {
    label: 'Spot Award',
    path: '/account-settings/spot-awards',
    portals: [],
  },
  help: {
    label: 'Help',
    path: '/account-settings/help',
    mobileIcon: faQuestionCircle,
    portals: [
      UserType.HS_STUDENT,
      UserType.CC_STUDENT,
      UserType.PARENT,
      UserType.UNDERGRADUATE_STUDENT,
      UserType.ADULT_LEARNER,
      UserType.EDUCATOR,
    ],
  },
};

const memoizedTabs: Record<UserType, AccountSettingsTab[]> = {} as Record<
  UserType,
  AccountSettingsTab[]
>;

export const accountSettingsTabs = async (portal: UserType): Promise<AccountSettingsTab[]> => {
  if (memoizedTabs[portal]) return memoizedTabs[portal];

  const portalRoutes = Object.values(ACCOUNT_SETTING_ROUTES).filter((route) =>
    route.portals.includes(portal)
  );

  const filteredRoutes = await Promise.all(
    portalRoutes.map(async (route) => {
      if (route.renderIf?.[portal]) {
        const shouldRender = await route.renderIf[portal]?.();
        return shouldRender ? route : null;
      }
      return route;
    })
  );

  const tabs = filteredRoutes.filter(Boolean) as AccountSettingsTab[];
  memoizedTabs[portal] = tabs;

  return tabs;
};

export const accountSettingsLabelFromPath = (pathname: string): string => {
  const route = pathname.split('/account-settings/')[1];

  if (!Object.keys(ACCOUNT_SETTING_ROUTES).includes(route)) return '';

  return ACCOUNT_SETTING_ROUTES[route].label;
};
